/* global React, Icon, Card, Button, Modal, fmt */
// ============================================================
// PLUMME 3 — Privat økonomi · Familie
// Husholdningsmedlemmer, fordeling av felleskostnader og felles
// grep. Alt regnes fra opplysningene medlemmene selv har lagt
// inn (inntekt og andel felleskostnader) — ingen antagelser.
// ============================================================
const { useState: pofamUseState } = React;

// Hvor skjev fordelingen er, målt i prosentpoeng mellom kostnadsandel
// og inntektsandel. Under 5 pp regner vi som jevnt.
function pofamFairness(gap) {
  const size = Math.abs(gap);
  if (size <= 5) return { color: 'var(--success)', label: t('pkn.fam.fair.even') };
  if (size <= 15) return { color: 'var(--warning)', label: t('pkn.fam.fair.slightly') };
  return { color: 'var(--error)', label: t('pkn.fam.fair.uneven') };
}

// Fordeling av felleskostnader målt mot inntektsandel.
function PoFamContributions({ members = [] }) {
  const Sec = window.PoSecHead;
  const Says = window.PlummaSays;
  const sharedTotal = members.reduce((sum, member) => sum + Number(member.monthlySharedCosts || 0), 0);
  const incomeTotal = members.reduce((sum, member) => sum + Number(member.monthlyIncome || 0), 0);
  const rows = members.map((member) => {
    const costShare = sharedTotal ? Number(member.monthlySharedCosts || 0) / sharedTotal * 100 : 0;
    const incomeShare = incomeTotal ? Number(member.monthlyIncome || 0) / incomeTotal * 100 : 0;
    return { costShare, gap: costShare - incomeShare, incomeShare, member, proportional: sharedTotal * incomeShare / 100 };
  });
  const widest = rows.length ? [...rows].sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))[0] : null;

  return <Card>
    <Sec icon="users" title={t('po.sharedCosts')} sub={t('po.sharedCostsSub')}
      help={t('po.sharedCostsHelp')} />
    {members.length < 2 || !sharedTotal
      ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.fam.needTwo')}</div>
      : <React.Fragment>
        <div className="po-bud-list" style={{ marginTop: 14 }}>
          {rows.map(({ costShare, gap, incomeShare, member }) => {
            const tone = pofamFairness(gap);
            return <div key={member.id}>
              <div className="po-bud-row-head">
                <span className="aii-cat-dot" style={{ background: tone.color, height: 12, width: 12 }} />
                <span className="po-bud-name">{member.name}</span>
                <span className="po-bud-amt">{fmt(member.monthlySharedCosts)} <span className="muted">{t('pkn.fam.perMonthShort')}</span></span>
              </div>
              <div className="po-bud-track"><div style={{ background: tone.color, width: `${Math.min(100, costShare)}%` }} /></div>
              <div className="po-bud-row-foot">
                <span className="po-bud-pct" style={{ color: tone.color }}>{t('pkn.fam.ofSharedCosts', { pct: Math.round(costShare) })}</span>
                <span className="po-bud-remain">{t('pkn.fam.ofIncome', { pct: Math.round(incomeShare) })}</span>
              </div>
            </div>;
          })}
        </div>
        {Says && widest && <Says>
          {Math.abs(widest.gap) <= 5
            ? <React.Fragment>{t('sharedText.po.splitIsEven')} <b>{fmt(sharedTotal)}</b> {t('pkn.fam.evenSuffix')}</React.Fragment>
            : <React.Fragment><b>{widest.member.name}</b> {t('pkn.fam.uneven', { costShare: Math.round(widest.costShare), incomeShare: Math.round(widest.incomeShare), amount: fmt(Math.round(widest.proportional)), diff: fmt(Math.abs(Math.round(widest.proportional - Number(widest.member.monthlySharedCosts || 0)))), direction: widest.gap > 0 ? t('pkn.fam.less') : t('pkn.fam.more') })}</React.Fragment>}
        </Says>}
      </React.Fragment>}
  </Card>;
}

// Felles grep, utledet fra husholdningens egne tall.
function PoFamTogether({ members = [], onAsk }) {
  const Sec = window.PoSecHead;
  const sharedTotal = members.reduce((sum, member) => sum + Number(member.monthlySharedCosts || 0), 0);
  const incomeTotal = members.reduce((sum, member) => sum + Number(member.monthlyIncome || 0), 0);
  const surplus = incomeTotal - sharedTotal;
  const widestGap = members.length > 1 && sharedTotal && incomeTotal
    ? Math.max(...members.map((member) => Math.abs(
      Number(member.monthlySharedCosts || 0) / sharedTotal * 100 - Number(member.monthlyIncome || 0) / incomeTotal * 100)))
    : 0;

  const ideas = [];
  if (surplus > 0) ideas.push({
    icon: 'wallet', title: t('pkn.fam.idea.bufferTitle'),
    text: t('pkn.fam.idea.bufferText', { left: fmt(Math.round(surplus)), monthly: fmt(Math.round(surplus * 0.1)), yearly: fmt(Math.round(surplus * 0.1 * 12)) }),
    metric: t('pkn.fam.metricPerMonth', { amount: fmt(Math.round(surplus * 0.1)) }),
  });
  if (widestGap > 15) ideas.push({
    icon: 'users', title: t('pkn.fam.idea.keyTitle'),
    text: t('pkn.fam.idea.keyText'),
    metric: t('pkn.fam.metricDeviation', { pp: Math.round(widestGap) }),
  });
  if (sharedTotal > 0) ideas.push({
    icon: 'receipt', title: t('pkn.fam.idea.reviewTitle'),
    text: t('pkn.fam.idea.reviewText', { monthly: fmt(sharedTotal), yearly: fmt(sharedTotal * 12) }),
    metric: t('pkn.fam.metricPerYear', { amount: fmt(sharedTotal * 12) }),
  });

  return <Card>
    <Sec icon="trendUp" title={t('po.togetherYouCan')} sub={t('po.togetherYouCanSub')}
      right={onAsk ? <button className="aii-hero-pill aii-hero-pill-btn" onClick={onAsk}><Icon name="sparkle" size={15} />{t('po.askMe')}</button> : null}
      help={t('po.togetherHelp')} />
    {ideas.length === 0
      ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.fam.ideasEmpty')}</div>
      : <div className="aii-reco-grid" style={{ marginTop: 14 }}>
        {ideas.map((idea) => <div key={idea.title} className="aii-reco opp">
          <div className="aii-reco-top"><span className="aii-reco-badge"><Icon name={idea.icon} size={13} />{t('po.sharedSteps')}</span></div>
          <div className="aii-reco-text"><b style={{ color: 'var(--ink)' }}>{idea.title}.</b> {idea.text}</div>
          <div className="aii-reco-impact"><span className="lbl">{t('po.magnitude')}</span><span className="val">{idea.metric}</span></div>
        </div>)}
      </div>}
  </Card>;
}

function PoHouseholdEditor({ member, onClose, onDelete, onSave }) {
  const [draft, setDraft] = pofamUseState(() => member || { id: '', monthlyIncome: '', monthlySharedCosts: '', name: '', role: '' });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const save = () => onSave({
    ...draft,
    id: draft.id || `member-${Date.now()}`,
    monthlyIncome: Math.max(0, Number(draft.monthlyIncome) || 0),
    monthlySharedCosts: Math.max(0, Number(draft.monthlySharedCosts) || 0),
    name: String(draft.name || '').trim(),
    role: String(draft.role || '').trim() || 'Husholdning',
  });
  return <Modal open onClose={onClose} max={560} title={member ? t('pkn.fam.editPerson') : t('pkn.fam.addPerson')}
    footer={<React.Fragment>{member && <Button variant="ghost" icon="trash" onClick={() => onDelete?.(member)}>{t('list.delete')}</Button>}<span style={{ flex: 1 }} /><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button disabled={!String(draft.name || '').trim()} onClick={save}>{t('sale.save')}</Button></React.Fragment>}>
    <div className="gj-form-grid">
      <label className="gj-field"><span>{t('cust.form.contactName')}</span><input className="gj-input" value={draft.name} onChange={(event) => set('name', event.target.value)} /></label>
      <label className="gj-field" data-help={t('helpTopics.familyRole.body')} data-help-title={t('helpTopics.familyRole.title')}><span  >{t('cust.form.contactRole')}</span><input className="gj-input" value={draft.role} placeholder={t('po.youPartnerPh')} onChange={(event) => set('role', event.target.value)} /></label>
      <label className="gj-field"><span>{t('po.monthlyNetIncome')}</span><Input className="gj-input" type="number" min="0" value={draft.monthlyIncome} onChange={(event) => set('monthlyIncome', event.target.value)} /></label>
      <label className="gj-field"><span>{t('po.sharedCostShare')}</span><Input className="gj-input" type="number" min="0" value={draft.monthlySharedCosts} onChange={(event) => set('monthlySharedCosts', event.target.value)} /></label>
    </div>
  </Modal>;
}

function PoFamilieConnected({ connection, household = { members: [] }, onAsk, onOpenHousehold, onSaveHousehold }) {
  const Sec = window.PoSecHead;
  const Says = window.PlummaSays;
  const [editing, setEditing] = pofamUseState(null);
  const members = household.members || [];
  const saveMembers = (next) => onSaveHousehold?.({ ...household, members: next });
  const saveMember = (member) => {
    saveMembers(members.some((item) => item.id === member.id) ? members.map((item) => item.id === member.id ? member : item) : [...members, member]);
    setEditing(null);
  };
  const total = members.reduce((sum, member) => sum + Number(member.monthlySharedCosts || 0), 0);
  const totalIncome = members.reduce((sum, member) => sum + Number(member.monthlyIncome || 0), 0);
  return <React.Fragment>
    {!connection && Says && <Says action={onOpenHousehold ? t('pkn.fam.goToMyPage') : null} onAction={onOpenHousehold}>{t('pkn.fam.devMode')}</Says>}
    {connection && members.length === 0 && Says && <Says action={onAsk ? t('pkn.fam.askSuperPlumma') : null} onAction={onAsk}>{t('pkn.fam.connectedEmpty')}</Says>}
    <Card>
      <Sec icon="users" title={t('po.familyFinances')} sub={connection ? t('pkn.fam.subShared') : t('pkn.fam.subLocal')} right={<Button size="sm" icon="plus" onClick={() => setEditing({})} data-help={t('helpTopics.household.body')} data-help-title={t('helpTopics.household.title')}>{t('po.addPerson')}</Button>} />
      {members.length === 0
        ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('po.noHouseholdMembers')}</div>
        : <div className="po-fam-grid" style={{ marginTop: 14 }}>
          {members.map((member) => <div key={member.id} className="po-fam-card" role="button" tabIndex={0} onClick={() => setEditing(member)} onKeyDown={(event) => { if (event.key === 'Enter') setEditing(member); }}>
            <div className="po-fam-top"><span className="po-fam-ava">{member.initials || String(member.name || '?').slice(0, 2).toUpperCase()}</span><div><div className="po-fam-name">{member.name}</div><div className="po-fam-role">{member.role}</div></div></div>
            <div className="po-fam-val">{fmt(member.monthlySharedCosts)}</div>
            <div className="po-fam-sub">{t('pkn.fam.cardSub', { income: fmt(member.monthlyIncome) })}</div>
            <div className="po-fam-sub">{t('pkn.fam.cardShares', { income: totalIncome ? Math.round(Number(member.monthlyIncome || 0) / totalIncome * 100) : 0, costs: total ? Math.round(Number(member.monthlySharedCosts || 0) / total * 100) : 0 })}</div>
          </div>)}
        </div>}
      {total > 0 && Says && <Says>{t('sharedText.po.householdHasRegistered')} <b>{fmt(total)}</b> {t('pkn.fam.householdSuffix')}</Says>}
    </Card>
    <PoFamContributions members={members} />
    <PoFamTogether members={members} onAsk={onAsk} />
    {editing && <PoHouseholdEditor member={editing.id ? editing : null} onClose={() => setEditing(null)} onSave={saveMember} onDelete={(member) => { saveMembers(members.filter((item) => item.id !== member.id)); setEditing(null); }} />}
  </React.Fragment>;
}

Object.assign(window, { PoFamContributions, PoFamilie: PoFamilieConnected, PoFamTogether });
